import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import DP, { registerLocale } from "react-datepicker";
import { enUS, ru } from "date-fns/locale";
import { clearDate } from "@utils";
import CalendarIcon from "@assets/icon_calendar.svg";
import { useAppDispatch, useAppSelector } from "@hooks";
import { showNotification } from "@store";
import { fields } from "./localization";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker.scss";
const MIN_YEAR = 1900;
export function DatePicker({ disabled, hasError, showMonthYearPicker, allowFutureDates = false, className, placeholderText, value, onChange, }) {
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const calendarBtnRef = useRef();
    const [innerDate, setInnerDate] = useState(value === null || value === undefined || (value === null || value === void 0 ? void 0 : value.getFullYear()) >= MIN_YEAR ? value : new Date());
    const [innerValue, setInnerValue] = useState("");
    const [hasInnerDateError, setHasInnerDateError] = useState(false);
    const { language } = useAppSelector((state) => state.ui);
    const dispatch = useAppDispatch();
    const field = fields(language);
    const formatDateValue = (date) => {
        const day = `${date.getDate()}`.padStart(2, "0");
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const year = date.getFullYear();
        return showMonthYearPicker ? `${month}/${year}` : `${day}/${month}/${year}`;
    };
    const parseInputValue = (str) => {
        const currentDate = new Date();
        const maxYear = allowFutureDates ? 2100 : currentDate.getFullYear();
        if (showMonthYearPicker) {
            const match = /^(\d{1,2})\/(\d{4})$/.exec(str);
            if (!match) {
                return null;
            }
            const monthNumber = Number(match[1]);
            const year = Number(match[2]);
            if (monthNumber < 1 || monthNumber > 12 || year < MIN_YEAR || year > maxYear) {
                return null;
            }
            const parsedMonth = new Date(year, monthNumber - 1, 1);
            if (!allowFutureDates) {
                const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                return parsedMonth > currentMonth ? null : parsedMonth;
            }
            return parsedMonth;
        }
        const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(str);
        if (!match) {
            return null;
        }
        const day = Number(match[1]);
        const monthNumber = Number(match[2]);
        const year = Number(match[3]);
        if (monthNumber < 1 || monthNumber > 12 || year < MIN_YEAR || year > maxYear) {
            return null;
        }
        const parsedDate = new Date(year, monthNumber - 1, day);
        const isInvalidDate = parsedDate.getFullYear() !== year ||
            parsedDate.getMonth() + 1 !== monthNumber ||
            parsedDate.getDate() !== day;
        if (isInvalidDate || (!allowFutureDates && parsedDate > currentDate)) {
            return null;
        }
        return parsedDate;
    };
    const isValid = (str) => {
        const minLength = showMonthYearPicker ? 6 : 8;
        const maxLength = showMonthYearPicker ? 7 : 10;
        if (!str || !/^[\d/]+$/.test(str) || str.length > maxLength || str.length < minLength) {
            return false;
        }
        return Boolean(parseInputValue(str));
    };
    useEffect(() => {
        setInnerDate(value);
        if (value === null || value === undefined) {
            setInnerValue("");
            setHasInnerDateError(false);
            if (value === undefined) {
                setHasInnerDateError(true);
            }
        }
        else {
            const calculatedValue = formatDateValue(value);
            if (isValid(calculatedValue)) {
                setHasInnerDateError(false);
                setInnerValue(calculatedValue);
            }
            else {
                onChange(undefined);
                dispatch(showNotification({ isSuccess: false, message: field.emptyDate }));
                setHasInnerDateError(true);
            }
        }
    }, [value, showMonthYearPicker]);
    const handleOutsideClick = (event) => {
        onChange(innerDate);
        if (isCalendarOpen && !calendarBtnRef.current.contains(event.target)) {
            setIsCalendarOpen(false);
        }
    };
    const handleInputChange = (event) => {
        setIsCalendarOpen(false);
        let str = event.target.value;
        if (str === "") {
            setInnerValue("");
            return;
        }
        const maxLength = showMonthYearPicker ? 7 : 10;
        if (!str || !/^[\d/]+$/.test(str) || str.length > maxLength) {
            return;
        }
        if (/^\d\d$/.exec(str) && !/^\d\d\/$/.exec(innerValue)) {
            str = `${str}/`;
        }
        if (!showMonthYearPicker &&
            /^\d\d\/\d\d$/.exec(str) &&
            !/^\d\d\/\d\d\/$/.exec(innerValue)) {
            str = `${str}/`;
        }
        setInnerValue(str);
        const minLength = showMonthYearPicker ? 6 : 8;
        if (!str || str.length < minLength) {
            setHasInnerDateError(true);
            return;
        }
        const parsedDate = parseInputValue(str);
        if (!parsedDate) {
            setHasInnerDateError(true);
            return;
        }
        setInnerDate(parsedDate);
        setHasInnerDateError(false);
        onChange(parsedDate);
    };
    registerLocale("ru", ru);
    registerLocale("en", enUS);
    return (React.createElement("div", { className: clsx(className, "datepicker-wrap", (hasError || hasInnerDateError) && "error") },
        React.createElement(DP, { disabled: disabled, selected: innerDate, value: innerValue, onChangeRaw: handleInputChange, onChange: (date) => {
                setHasInnerDateError(false);
                if (date) {
                    onChange(new Date(clearDate(date.toISOString())));
                }
                else {
                    onChange(undefined);
                }
            }, open: isCalendarOpen, minDate: new Date(`01/01/${MIN_YEAR}`), maxDate: allowFutureDates ? undefined : new Date(), dateFormat: showMonthYearPicker ? "MM/yyyy" : "dd/MM/yyyy", placeholderText: placeholderText, onInputClick: () => setIsCalendarOpen(!isCalendarOpen), onClickOutside: handleOutsideClick, showYearDropdown: true, dropdownMode: "select", showMonthYearPicker: showMonthYearPicker, locale: field.locale, shouldCloseOnSelect: true, autoComplete: "off" }),
        React.createElement("button", { className: "datepicker__calendar-icon-box", type: "button", ref: calendarBtnRef, disabled: disabled, onClick: () => {
                if (!disabled) {
                    setIsCalendarOpen(!isCalendarOpen);
                }
            } },
            React.createElement(CalendarIcon, { className: "datepicker__calendar-icon" }))));
}
